import React, { useCallback, memo } from "react";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { usePopup } from "../../Context/PopupContext";
import { useAlert } from "../../Context/AlertContext";

// Create white theme for MUI components - white text, labels, and underlines
const whiteTheme = createTheme({
  palette: {
    mode: "dark", // Using dark mode to enable white text
    primary: {
      main: "#ffffff",
      contrastText: "#000000",
    },
    secondary: {
      main: "#f5f5f5",
      contrastText: "#000000",
    },
    text: {
      primary: "#ffffff", // White text
      secondary: "#e0e0e0", // Light gray white for secondary text
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            color: "#ffffff", // White text input
            "&:before": {
              borderBottomColor: "#ffffff", // White underline
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottomColor: "#e0e0e0", // Light white shade on hover
            },
            "&:after": {
              borderBottomColor: "#f5f5f5", // Even lighter white when focused
            },
          },
          "& .MuiInputLabel-root": {
            color: "#ffffff", // White label
            "&.Mui-focused": {
              color: "#f5f5f5", // Light white when focused
            },
            "&.Mui-error": {
              color: "#ffcdd2", // Light red for errors
            },
          },
          "& .MuiFormHelperText-root": {
            color: "#e0e0e0", // Light white for helper text
            "&.Mui-error": {
              color: "#ffcdd2", // Light red for error messages
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            color: "#ffffff", // White labels
            "&.Mui-focused": {
              color: "#f5f5f5", // Light white when focused
            },
            "&.Mui-error": {
              color: "#ffcdd2", // Light red for errors
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: "#ffffff", // White text in select
          "&:before": {
            borderBottomColor: "#ffffff", // White underline
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: "#e0e0e0", // Light white shade on hover
          },
          "&:after": {
            borderBottomColor: "#f5f5f5", // Even lighter white when focused
          },
        },
        icon: {
          color: "#ffffff", // White dropdown arrow
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#ffffff", // White labels
          textTransform: "uppercase", // Prevent uppercase transformation
          fontSize: "12px",
          letterSpacing: "2.5px", // Prevent uppercase transformation
          "&.Mui-focused": {
            color: "#f5f5f5", // Light white when focused
          },
          "&.Mui-error": {
            color: "#ffcdd2", // Light red for errors
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#ffffff", // White text in menu items
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)", // Subtle white hover
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: "#e0e0e0", // Light white for helper text
          "&.Mui-error": {
            color: "#ffcdd2", // Light red for error messages
          },
        },
      },
    },
  },
});

// Validation schema - memoized for performance
const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .required("Name is required"),
  email: Yup.string()
    .email("Must be a valid email")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Phone must be exactly 10 digits")
    .required("Phone is required"),
  type: Yup.string()
    .oneOf(["buy", "rent", "sell"], "Please select a valid type")
    .required("Type is required"),
  property: Yup.string()
    .oneOf(
      ["apartment", "villa", "land"],
      "Please select a valid property type"
    )
    .required("Property type is required"),
  message: Yup.string()
    .max(500, "Message must be less than 500 characters")
    .optional(),
});

// Initial form values
const initialValues = {
  name: "",
  email: "",
  phone: "",
  type: "",
  property: "",
  message: "",
};

// Form options
const typeOptions = [
  { value: "buy", label: "Buy" },
  { value: "rent", label: "Rent" },
  { value: "sell", label: "Sell" },
];

const propertyOptions = [
  { value: "apartment", label: "Apartment" },
  { value: "villa", label: "Villa" },
  { value: "land", label: "Land" },
];

// Main Enquiry Form Component
const PopEnquiryForm = memo(
  ({
    requestCallbackText = "Request Callback",
    onSubmitSuccess,
    onSubmitError,
  }) => {
    const { closePopup } = usePopup();
    const { showAlert } = useAlert();

    // Optimized submit handler
    const handleSubmit = useCallback(
      async (values, { setSubmitting, resetForm, setStatus }) => {
        try {
          setStatus(null);

          // Simulate API call - replace with actual API endpoint
          await new Promise((resolve) => setTimeout(resolve, 1500));

          // For demo - randomly simulate success/failure
          const isSuccess = Math.random() > 0.1; // 90% success rate

          if (isSuccess) {
            showAlert(
              "success",
              "Your enquiry has been submitted successfully! We will contact you soon."
            );
            resetForm();
            closePopup();

            // Call success callback if provided
            onSubmitSuccess?.(values);
          } else {
            throw new Error("Failed to submit enquiry. Please try again.");
          }
        } catch (error) {
          console.error("Form submission error:", error);
          showAlert(
            "error",
            error.message || "Something went wrong. Please try again."
          );
          setStatus({ error: error.message });

          // Call error callback if provided
          onSubmitError?.(error, values);
        } finally {
          setSubmitting(false);
        }
      },
      [showAlert, closePopup, onSubmitSuccess, onSubmitError]
    );

    return (
      <ThemeProvider theme={whiteTheme}>
        <div className="container">
          {/* Header */}
          <div className="text-center mb-4">
            <h3 className=" heading_enquiry" style={{ color: "#ffffff" }}>
              {requestCallbackText}
            </h3>
            <p className="text-muted" style={{ color: "#e0e0e0" }}>
              Fill out the form below and we'll get back to you shortly
            </p>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              status,
            }) => (
              <FormikForm>
                <div className="form_Conainer ">
                  {/* First Row - 3 inputs */}
                  <div className=" form_row">
                    <div className="w_30 padding_line_2">
                      <TextField
                        fullWidth
                        id="name"
                        name="name"
                        label="Full Name *"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        disabled={isSubmitting}
                        variant="standard"
                      />
                    </div>
                    <div className="w_30 padding_line_2 ">
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        type="email"
                        label="Email Address *"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        disabled={isSubmitting}
                        variant="standard"
                      />
                    </div>
                    <div className="w_30 padding_line_2 ">
                      <TextField
                        fullWidth
                        id="phone"
                        name="phone"
                        type="tel"
                        label="Phone Number *"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        disabled={isSubmitting}
                        variant="standard"
                        inputProps={{
                          maxLength: 10,
                          pattern: "[0-9]*",
                        }}
                      />
                    </div>
                  </div>

                  {/* Second Row - 2 select boxes */}
                  <div className="form_row">
                    <div className="w_50 padding_line_2 ">
                      <FormControl
                        fullWidth
                        error={touched.type && Boolean(errors.type)}
                        disabled={isSubmitting}
                        variant="standard"
                      >
                        <InputLabel id="type-label">Enquiry Type *</InputLabel>
                        <Select
                          labelId="type-label"
                          id="type"
                          name="type"
                          value={values.type}
                          label="Enquiry Type *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="standard"
                        >
                          {typeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.type && errors.type && (
                          <FormHelperText>{errors.type}</FormHelperText>
                        )}
                      </FormControl>
                    </div>
                    <div className="w_50 padding_line_2 ">
                      <FormControl
                        fullWidth
                        error={touched.property && Boolean(errors.property)}
                        disabled={isSubmitting}
                        variant="standard"
                      >
                        <InputLabel id="property-label">
                          Property Type *
                        </InputLabel>
                        <Select
                          labelId="property-label"
                          id="property"
                          name="property"
                          value={values.property}
                          label="Property Type *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="standard"
                        >
                          {propertyOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.property && errors.property && (
                          <FormHelperText>{errors.property}</FormHelperText>
                        )}
                      </FormControl>
                    </div>
                  </div>

                  {/* Third Row - Full width message field */}
                  <div className="padding_line_2 col-12 ">
                    <TextField
                      fullWidth
                      id="message"
                      name="message"
                      label="Additional Message (Optional)"
                      multiline
                      rows={2}
                      value={values.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      disabled={isSubmitting}
                      variant="standard"
                      placeholder="Tell us more about your requirements..."
                    />
                  </div>

                  {/* Error Status */}
                  {status?.error && (
                    <div className="mb-3 p-2 bg-danger bg-opacity-10 text-danger rounded">
                      <p className="mb-0">{status.error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="my-3">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 fw-semibold py-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              </FormikForm>
            )}
          </Formik>
        </div>
      </ThemeProvider>
    );
  }
);

export default PopEnquiryForm;
